import React, { useState, useEffect } from "react";
import axios from "axios";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Card, CardHeader, CardTitle, CardContent } from "../ui/card";
import { Badge } from "../ui/badge";
import { Loader2, FileWarning, CheckCircle2, RefreshCw, Download } from "lucide-react";
import { localTodayISO, localDateISO } from "../../lib/localDate";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmtDate = (d) => {
  try {
    const [y, m, day] = d.split("-").map(Number);
    return new Date(y, m - 1, day).toLocaleDateString("en-AU", {
      weekday: "short", day: "numeric", month: "short", year: "numeric",
    });
  } catch { return d; }
};

const todayISO = () => localTodayISO();
const daysAgoISO = (n) => {
  const d = new Date();
  d.setDate(d.getDate() - n);
  return localDateISO(d);
};

/**
 * Admin/manager/supervisor compliance roll-up: every missed daily toolbox
 * sign-off in the window, with the worker's reason if they explained it.
 * Embeddable as a tab inside AnalyticsPage.
 */
export const MissedSignoffsReportTab = () => {
  const [startDate, setStartDate] = useState(daysAgoISO(30));
  const [endDate, setEndDate] = useState(todayISO());
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState({ items: [], summary: { total: 0, explained: 0, no_reason: 0 } });
  const [filterStatus, setFilterStatus] = useState("all"); // all | no_reason | explained

  const fetchReport = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/missed-signoffs/report`, {
        params: { start_date: startDate, end_date: endDate },
      });
      setData(res.data || { items: [], summary: { total: 0, explained: 0, no_reason: 0 } });
    } catch (e) {
      console.error("Failed to fetch missed sign-offs report", e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchReport(); /* eslint-disable-next-line */ }, []);

  const filteredItems = (data.items || []).filter((i) =>
    filterStatus === "all" ? true : i.status === filterStatus
  );

  const exportCSV = () => {
    const headers = ["Date", "Job", "Toolbox / Form", "Worker", "Status", "Reason", "Reason Submitted"];
    const rows = filteredItems.map((i) => [
      i.date,
      i.job_title || "",
      i.template_name || "",
      i.user_name || "",
      i.status === "explained" ? "Explained" : "No reason yet",
      (i.reason || "").replace(/"/g, '""'),
      i.reason_submitted_at || "",
    ]);
    const csv = [headers, ...rows]
      .map((r) => r.map((c) => `"${String(c ?? "")}"`).join(","))
      .join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `missed-signoffs_${startDate}_${endDate}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="space-y-4" data-testid="missed-signoffs-report-tab">
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base font-bold uppercase tracking-tight flex items-center gap-2">
            <FileWarning className="w-5 h-5 text-amber-500" />
            Missed Toolbox Sign-offs
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="flex flex-wrap items-end gap-2">
            <div>
              <Label className="text-xs">From</Label>
              <Input
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                className="h-9"
                data-testid="missed-report-start-date"
              />
            </div>
            <div>
              <Label className="text-xs">To</Label>
              <Input
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                className="h-9"
                data-testid="missed-report-end-date"
              />
            </div>
            <Button
              onClick={fetchReport}
              disabled={loading}
              className="h-9 bg-orange-500 hover:bg-orange-600 text-white"
              data-testid="missed-report-refresh-btn"
            >
              {loading ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <RefreshCw className="w-4 h-4 mr-1" />}
              Refresh
            </Button>
            <Button
              onClick={exportCSV}
              variant="outline"
              className="h-9"
              disabled={!filteredItems.length}
              data-testid="missed-report-export-btn"
            >
              <Download className="w-4 h-4 mr-1" /> Export CSV
            </Button>
          </div>

          <div className="grid grid-cols-3 gap-3">
            <button
              onClick={() => setFilterStatus("all")}
              className={`rounded-lg p-3 text-left border-2 transition ${
                filterStatus === "all" ? "border-slate-900 bg-slate-50" : "border-slate-200 hover:border-slate-400"
              }`}
              data-testid="missed-report-filter-all"
            >
              <div className="text-xs uppercase text-slate-500 font-semibold">Total Missed</div>
              <div className="text-2xl font-bold">{data.summary?.total ?? 0}</div>
            </button>
            <button
              onClick={() => setFilterStatus("no_reason")}
              className={`rounded-lg p-3 text-left border-2 transition ${
                filterStatus === "no_reason" ? "border-red-500 bg-red-50" : "border-slate-200 hover:border-red-300"
              }`}
              data-testid="missed-report-filter-no-reason"
            >
              <div className="text-xs uppercase text-red-600 font-semibold">No Reason Yet</div>
              <div className="text-2xl font-bold text-red-600">{data.summary?.no_reason ?? 0}</div>
            </button>
            <button
              onClick={() => setFilterStatus("explained")}
              className={`rounded-lg p-3 text-left border-2 transition ${
                filterStatus === "explained" ? "border-green-500 bg-green-50" : "border-slate-200 hover:border-green-300"
              }`}
              data-testid="missed-report-filter-explained"
            >
              <div className="text-xs uppercase text-green-700 font-semibold">Explained</div>
              <div className="text-2xl font-bold text-green-700">{data.summary?.explained ?? 0}</div>
            </button>
          </div>

          {loading ? (
            <div className="py-10 text-center text-slate-500"><Loader2 className="w-6 h-6 animate-spin mx-auto" /></div>
          ) : filteredItems.length === 0 ? (
            <div className="py-10 text-center text-slate-500 flex flex-col items-center gap-2">
              <CheckCircle2 className="w-8 h-8 text-green-500" />
              <div>No missed sign-offs in this window. Good as gold!</div>
            </div>
          ) : (
            <div className="border rounded-lg overflow-hidden" data-testid="missed-report-table">
              <table className="w-full text-sm">
                <thead className="bg-slate-100 text-xs uppercase">
                  <tr>
                    <th className="text-left px-3 py-2">Date</th>
                    <th className="text-left px-3 py-2">Job</th>
                    <th className="text-left px-3 py-2">Toolbox / Form</th>
                    <th className="text-left px-3 py-2">Worker</th>
                    <th className="text-left px-3 py-2">Status</th>
                    <th className="text-left px-3 py-2">Reason</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredItems.map((i, ix) => (
                    <tr key={`${i.allocation_id}-${i.user_id}-${i.date}-${ix}`} className="border-t">
                      <td className="px-3 py-2 whitespace-nowrap">{fmtDate(i.date)}</td>
                      <td className="px-3 py-2">{i.job_title || "—"}</td>
                      <td className="px-3 py-2">{i.template_name || "—"}</td>
                      <td className="px-3 py-2">{i.user_name || "—"}</td>
                      <td className="px-3 py-2">
                        {i.status === "explained" ? (
                          <Badge className="bg-green-500 text-white">Explained</Badge>
                        ) : (
                          <Badge className="bg-red-500 text-white">No reason</Badge>
                        )}
                      </td>
                      <td className="px-3 py-2 text-slate-700 max-w-md">
                        {i.reason ? (
                          <span title={i.reason_submitted_at || ""}>{i.reason}</span>
                        ) : (
                          <span className="italic text-slate-400">— no reason provided —</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default MissedSignoffsReportTab;
